<template>
    <div class="CreateTeamChallange">
        <div class="page-wrapper">
            <h3> Create New Weekly Goal </h3>
            <div class="wrapper">
                <div class="left-wrapper">
                    <div class="top-part-wrapper">
                        <nav>
                            <a href="#" class="active">ALL JOURNEYS</a>
                            <a href="#">RECOMMENDED JOURNEYS</a>
                        </nav>
                        <div class="right-switch-wrapper">
                            <p>HIDE COMPLETED</p>
                            <div class="switch" @click="hideCompleted = !hideCompleted">
                                <div
                                    class="switch-button"
                                    :class="{
                                        'switch-right': hideCompleted,
                                        'switch-left': !hideCompleted
                                    }"
                                />
                            </div>
                        </div>
                    </div>
                    <JourneysList :done-journeys-ids="doneJourneysIds" @journeySelected="setJourneyId($event)" />
                </div>
                <div class="right-wrapper">
                    <form action="#" @submit.prevent="handleSubmit">
                        <h5>PICK DATE</h5>
                        <div class="date-inputs">
                            <div class="input-wrapper">
                                <fieldset>
                                    <legend>From</legend>
                                    <input v-model="formData.start" type="date" />
                                </fieldset>

                                <error-output v-if="showErrors" :errors="startDateErrors" />
                            </div>

                            <div class="input-wrapper">
                                <fieldset>
                                    <legend>To</legend>
                                    <input v-model="formData.stop" type="date" />
                                </fieldset>

                                <error-output v-if="showErrors" :errors="endDateErrors" />
                            </div>
                        </div>
                        <p v-if="formData.endDate" class="quiz-date-info">
                            Journey Quiz will be available at {{ formattedEndDate }}
                        </p>
                        <h5>SHOUTOUT</h5>
                        <textarea
                            v-model="formData.shoutout"
                            placeholder="Leave a personal message to your team mates"
                        />
                        <button>Create Now</button>
                        <error-output v-if="showErrors" :errors="selectedJourneyErrors" />
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import { mapGetters } from 'vuex';

import apolloClient from '@/apollo-client';
import getTeamsList from './graphql/GetTeamsList.gql';
import gql from 'graphql-tag';

import ErrorOutput from './ErrorOutput';
import JourneysList from './JourneysList';

const mustBeAfterStartDate = (value, formData) => {
    const startDate = moment(formData.start);
    const endDate = moment(formData.stop);

    return startDate.diff(endDate) < 0;
};

export default {
    components: {
        ErrorOutput,
        JourneysList
    },

    mixins: [validationMixin],

    data() {
        return {
            hideCompleted: false,
            showErrors: false,
            teams: [],
            teamGoals: [],
            formData: {
                journeyId: null,
                teamId: null,
                createdBy: null,
                start: null,
                stop: null,
                shoutout: null
            }
        };
    },

    validations: {
        formData: {
            journeyId: { required },
            start: { required },
            stop: { required, mustBeAfterStartDate }
        }
    },

    computed: {
        ...mapGetters({
            profile: 'moduleAuth/profile'
        }),

        formattedEndDate() {
            return moment(this.formData.end).format('Do MMMM YYYY');
        },

        startDateErrors() {
            const errors = [];

            if (!this.$v.formData.start.required) {
                errors.push('Field required');
            }

            return errors;
        },

        endDateErrors() {
            const errors = [];

            if (!this.$v.formData.stop.required) {
                errors.push('Field required');
            }

            if (!this.$v.formData.stop.mustBeAfterStartDate) {
                errors.push('This date must be after start date');
            }

            return errors;
        },

        selectedJourneyErrors() {
            const errors = [];

            if (!this.$v.formData.journeyId.required) {
                errors.push('Please select journey');
            }

            return errors;
        },

        doneJourneysIds() {
            return this.teamGoals
                .filter(teamGoal => {
                    const isTeamGoalCompleted =
                        teamGoal.quizGameId &&
                        this.$store.state.moduleMemoransier.games.find(game => game.id === teamGoal.quizGameId)
                            .leaderboardReveal;

                    return isTeamGoalCompleted;
                })
                .map(teamGoal => teamGoal.journeyId);
        }
    },

    async mounted() {
        await this.$store.dispatch('moduleMemoransier/refresh');

        await this.setTeamsList();

        this.setAditionalFormData();

        const response = await apolloClient.query({
            fetchPolicy: 'no-cache',
            query: gql`
                query {
                    teamGoalsList {
                        id
                        shoutout
                        start
                        stop
                        memoGameId
                        quizGameId
                        teamId
                        journeyId
                        createdBy
                    }
                }
            `
        });

        this.teamGoals = response.data.teamGoalsList;
    },

    methods: {
        async handleSubmit() {
            try {
                this.$v.$touch();

                if (this.$v.$invalid) {
                    this.showErrors = true;

                    return;
                }

                await apolloClient.mutate({
                    fetchPolicy: 'no-cache',
                    mutation: gql`
                        mutation CreateTeamGoalMutation(
                            $createdBy: String!
                            $shoutout: String
                            $start: Datetime
                            $stop: Datetime
                            $teamId: Int!
                            $journeyId: String!
                        ) {
                            __typename
                            createTeamGoal(
                                input: {
                                    teamGoal: {
                                        createdBy: $createdBy
                                        shoutout: $shoutout
                                        start: $start
                                        stop: $stop
                                        teamId: $teamId
                                        journeyId: $journeyId
                                    }
                                }
                            ) {
                                clientMutationId
                                teamGoal {
                                    shoutout
                                    start
                                    stop
                                    teamId
                                    journeyId
                                    createdBy
                                }
                            }
                        }
                    `,
                    variables: {
                        ...this.formData
                    }
                });

                this.$router.push({
                    name: 'DashboardHomeView'
                });
            } catch (error) {
                console.error(error);
            }
        },

        setJourneyId(id) {
            this.formData.journeyId = id;
        },

        setAditionalFormData() {
            this.formData.createdBy = this.profile.id;
            this.formData.teamId = this.$route.params.teamId;
        },

        async setTeamsList() {
            try {
                const response = await apolloClient.query({
                    query: getTeamsList
                });

                this.teams = response.data.teamsList;
            } catch (error) {
                console.error(error);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.CreateTeamChallange {
    background: linear-gradient(180deg, #0a1335 0%, #1e1456 93.87%);
    padding: 180px;
    overflow: scroll !important;
    .page-wrapper {
        max-width: 1440px;
        margin: 0 auto;
        h3 {
            font-weight: 500;
            font-size: 20px;
            line-height: 20px;
            color: #c9cfdc;
            margin-bottom: 35px;
        }
        .wrapper {
            display: flex;
            .left-wrapper {
                width: 60%;
                border-right: 1px solid rgba(33, 41, 55, 0.5);
                .top-part-wrapper {
                    display: flex;
                    justify-content: space-between;
                    border-width: 1px 0;
                    border-color: rgba(33, 41, 55, 0.5);
                    border-style: solid;
                    padding-top: 40px;
                    padding-bottom: 20px;
                    nav {
                        a {
                            font-weight: normal;
                            font-size: 10px;
                            line-height: 15px;
                            letter-spacing: 0.1em;
                            margin-right: 20px;
                            color: #a6b1cb;
                            text-decoration: none;
                            &.active {
                                color: #40ace0;
                            }
                        }
                    }
                    .right-switch-wrapper {
                        display: flex;
                        align-items: center;
                        p {
                            font-weight: normal;
                            font-size: 10px;
                            line-height: 15px;
                            text-align: right;
                            letter-spacing: 0.1em;
                            color: #a6b1cb;
                            opacity: 0.5;
                        }
                        .switch {
                            width: 25px;
                            height: 10px;
                            background: #404650;
                            border-radius: 20.093px;
                            display: flex;
                            align-items: center;
                            margin-left: 10px;
                            cursor: pointer;
                            margin-right: 70px;
                            .switch-button {
                                width: 15px;
                                height: 15px;
                                border-radius: 50%;
                                background: #5983f7;
                                box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);
                                transition: 0.5s;

                                &.switch-right {
                                    transform: translateX(0);
                                }
                                &.switch-left {
                                    transform: translateX(100%);
                                }
                            }
                        }
                    }
                }
            }
            .right-wrapper {
                padding: 30px;
                border-top: 1px solid rgba(33, 41, 55, 0.5);
                width: 40%;

                form {
                    h5 {
                        margin: 0;
                        font-weight: normal;
                        font-size: 10px;
                        line-height: 11px;
                        letter-spacing: 0.1em;
                        color: #a6b1cb;
                        margin-bottom: 20px;
                        &:nth-of-type(2) {
                            margin-top: 30px;
                        }
                    }

                    .date-inputs {
                        display: flex;
                        fieldset {
                            background: #18194d;
                            flex: 1;
                            border: 1px solid #a6b1cb;
                            margin-left: 0;
                            &:first-of-type {
                                margin-right: 10px;
                            }
                            legend {
                                color: #a6b1cb;
                                font-size: 13px;
                            }
                            input {
                                background: #18194d;
                                outline: none;
                                border: none;
                                color: #a6b1cb;
                                width: 100%;
                                &::-webkit-calendar-picker-indicator {
                                    filter: invert(80%);
                                    cursor: pointer;
                                }
                            }
                        }
                    }
                    p {
                        color: #a4b1cd;
                        opacity: 0.5;
                        font-size: 10px;
                        line-height: 15px;
                    }
                    textarea {
                        width: 100%;
                        resize: none;
                        height: 150px;
                        background: #18194d;
                        border: none;
                        border-radius: 2px;
                        padding: 15px;
                        color: #a6b1cb;
                        font-size: 13px;
                        &::placeholder {
                            color: #a6b1cb;
                            font-size: 13px;
                        }
                    }
                    button {
                        background: #40ace0;
                        border-radius: 4.76651px;
                        width: 100%;
                        height: 60px;
                        border: none;
                        font-weight: 500;
                        font-size: 16px;
                        line-height: 19px;
                        color: rgba(255, 255, 255, 0.9);
                        margin-top: 20px;
                        cursor: pointer;
                    }
                }
            }
        }
    }
}
</style>
